import { useState, useEffect, Dispatch, SetStateAction } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { CertRecord, Part3Record, CertBasicInfo } from "../types/cert";
import { generateCertDetailsMock } from "../utils/mockData";

const INITIAL_VISIBLE_COUNT = 1;
const INCREMENT_COUNT = 3;

const INITIAL_BASIC_INFO: CertBasicInfo = {
  fsdRcNumber: "",
  companyName: "",
  username: "",
  serialNumber: "",
  nameOfClient: "",
  typeOfBuilding: "",
  displayAddress: "",
  typeOfPremises: "",
};

function CertDetails() {
  const { t, i18n } = useTranslation();

  const [basicInfo, setBasicInfo] = useState<CertBasicInfo>(INITIAL_BASIC_INFO);
  const [part1Data, setPart1Data] = useState<CertRecord[]>([]);
  const [part2Data, setPart2Data] = useState<CertRecord[]>([]);
  const [part3Data, setPart3Data] = useState<Part3Record[]>([]);

  useEffect(() => {
    const mockData = generateCertDetailsMock();
    setBasicInfo(mockData.basicInfo || INITIAL_BASIC_INFO);
    setPart1Data(mockData.part1Data);
    setPart2Data(mockData.part2Data);
    setPart3Data(mockData.part3Data);
  }, []);

  const [part1VisibleCount, setPart1VisibleCount] = useState(
    INITIAL_VISIBLE_COUNT,
  );
  const [part2VisibleCount, setPart2VisibleCount] = useState(
    INITIAL_VISIBLE_COUNT,
  );
  const [part3VisibleCount, setPart3VisibleCount] = useState(
    INITIAL_VISIBLE_COUNT,
  );

  const handleShowMore = (
    currentCount: number,
    totalCount: number,
    setVisibleCount: Dispatch<SetStateAction<number>>,
  ) => {
    if (currentCount >= totalCount) {
      setVisibleCount(INITIAL_VISIBLE_COUNT); // Hide/Collapse
    } else {
      setVisibleCount((prev) => prev + INCREMENT_COUNT);
    }
  };

  return (
    <Box sx={{ bgcolor: "white", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#b71c1c",
          p: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Placeholder for Logo */}
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              sx={{
                height: 40,
              }}
              alt={"FSD LOGO"}
              src={`${process.env.PUBLIC_URL}/static/hkfsd-logo/HKFSD_LOGO_300X300.png`}
            /> 
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "white", display: "block", lineHeight: 1.2 }}
            >
              {t("certDetails.header.fsd")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "white",
                display: "block",
                fontSize: "0.6rem",
                opacity: 0.8,
              }}
            >
              {t("certDetails.header.gov")}
            </Typography>
          </Box>
        </Box>
        <Select
          value={
            i18n.language === "zh-HK"
              ? "zh-HK"
              : i18n.language === "zh-CN"
                ? "zh-CN"
                : "en"
          }
          onChange={(e) => i18n.changeLanguage(e.target.value)}
          size="small"
          sx={{ bgcolor: "white", height: 30, fontSize: "0.8rem" }}
        >
          <MenuItem value="en">Eng</MenuItem>
          <MenuItem value="zh-HK">繁体</MenuItem>
          <MenuItem value="zh-CN">简体</MenuItem>
        </Select>
      </Box>

      <Container maxWidth="md" sx={{ py: 2 }}>
        {/* Title */}
        <Typography
          variant="subtitle1"
          component="h1"
          sx={{
            color: "#d32f2f",
            fontWeight: "bold",
            mb: 0.5,
            lineHeight: 1.2,
          }}
        >
          {t("certDetails.title.reg")}
        </Typography>
        <Typography
          variant="subtitle1"
          component="h2"
          sx={{ color: "#d32f2f", fontWeight: "bold", mb: 2, lineHeight: 1.2 }}
        >
          {t("certDetails.title.cert")}
        </Typography>

        {/* Basic Info */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.basicInfo.fsdRcNumber")}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{basicInfo.fsdRcNumber}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.basicInfo.companyName")}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{basicInfo.companyName}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.basicInfo.username")}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{basicInfo.username}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1, borderBottomWidth: 1, borderColor: "black" }} />

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.basicInfo.serialNumber")}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{basicInfo.serialNumber}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.basicInfo.nameOfClient")}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{basicInfo.nameOfClient}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.basicInfo.typeOfBuilding")}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{basicInfo.typeOfBuilding}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.basicInfo.displayAddress")}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">
                    {basicInfo.displayAddress}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={5}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.basicInfo.typeOfPremises")}
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2">{basicInfo.typeOfPremises}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Part 1 */}
        <Accordion defaultExpanded sx={{ mb: 1, border: "1px solid black" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="part1-content"
            id="part1-header"
            sx={{
              bgcolor: "#f5f5f5",
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              minHeight: 48,
              ".MuiAccordionSummary-content": { my: 1 },
            }}
          >
            <Typography variant="subtitle1" color="error" fontWeight="bold">
              {t("certDetails.parts.part1")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {part1Data.slice(0, part1VisibleCount).map((record, index) => (
              <Card
                key={index}
                variant="outlined"
                sx={{ m: 1, border: "1px solid black" }}
              >
                <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.code")}</strong>{" "}
                    {record.code}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.location")}</strong>{" "}
                    {record.location}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>
                      {t("certDetails.fields.commentOnCondition")}
                    </strong>{" "}
                    {record.condition}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.completionDate")}</strong>{" "}
                    {record.completionDate}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.nextDueDate")}</strong>{" "}
                    {record.nextDueDate}
                  </Typography>
                </CardContent>
              </Card>
            ))}

            {part1Data.length > INITIAL_VISIBLE_COUNT && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                <Button
                  onClick={() =>
                    handleShowMore(
                      part1VisibleCount,
                      part1Data.length,
                      setPart1VisibleCount,
                    )
                  }
                  variant="text"
                  size="small"
                >
                  {part1VisibleCount >= part1Data.length
                    ? t("certDetails.actions.hide")
                    : t("certDetails.actions.more")}
                </Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Part 2 */}
        <Accordion defaultExpanded sx={{ mb: 1, border: "1px solid black" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="part2-content"
            id="part2-header"
            sx={{
              bgcolor: "#f5f5f5",
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              minHeight: 48,
              ".MuiAccordionSummary-content": { my: 1 },
            }}
          >
            <Typography variant="subtitle1" color="error" fontWeight="bold">
              {t("certDetails.parts.part2")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {part2Data.slice(0, part2VisibleCount).map((record, index) => (
              <Card
                key={index}
                variant="outlined"
                sx={{ m: 1, border: "1px solid black" }}
              >
                <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.code")}</strong>{" "}
                    {record.code}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.location")}</strong>{" "}
                    {record.location}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>
                      {t("certDetails.fields.commentOnCondition")}
                    </strong>{" "}
                    {record.condition}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.completionDate")}</strong>{" "}
                    {record.completionDate}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.nextDueDate")}</strong>{" "}
                    {record.nextDueDate}
                  </Typography>
                </CardContent>
              </Card>
            ))}

            {part2Data.length > INITIAL_VISIBLE_COUNT && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                <Button
                  onClick={() =>
                    handleShowMore(
                      part2VisibleCount,
                      part2Data.length,
                      setPart2VisibleCount,
                    )
                  }
                  variant="text"
                  size="small"
                >
                  {part2VisibleCount >= part2Data.length
                    ? t("certDetails.actions.hide")
                    : t("certDetails.actions.more")}
                </Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Part 3 */}
        <Accordion defaultExpanded sx={{ mb: 1, border: "1px solid black" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="part3-content"
            id="part3-header"
            sx={{
              bgcolor: "#f5f5f5",
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              minHeight: 48,
              ".MuiAccordionSummary-content": { my: 1 },
            }}
          >
            <Typography variant="subtitle1" color="error" fontWeight="bold">
              {t("certDetails.parts.part3")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {part3Data.slice(0, part3VisibleCount).map((record, index) => (
              <Card
                key={index}
                variant="outlined"
                sx={{ m: 1, border: "1px solid black" }}
              >
                <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.code")}</strong>{" "}
                    {record.code}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.location")}</strong>{" "}
                    {record.location}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>
                      {t("certDetails.fields.outstandingDefects")}
                    </strong>{" "}
                    {record.outstandingDefects}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.commentOnDefects")}</strong>{" "}
                    {record.commentOnDefects}
                  </Typography>
                  <Typography variant="body2" component="div">
                    <strong>{t("certDetails.fields.notification")}</strong>{" "}
                    {record.notification || "N/A"}
                  </Typography>
                </CardContent>
              </Card>
            ))}

            {part3Data.length > INITIAL_VISIBLE_COUNT && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                <Button
                  onClick={() =>
                    handleShowMore(
                      part3VisibleCount,
                      part3Data.length,
                      setPart3VisibleCount,
                    )
                  }
                  variant="text"
                  size="small"
                >
                  {part3VisibleCount >= part3Data.length
                    ? t("certDetails.actions.hide")
                    : t("certDetails.actions.more")}
                </Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Footer */}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={4}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.footer.remarks")}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">N/A</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={4}>
                  <Typography variant="body2" fontWeight="bold">
                    {t("certDetails.footer.tel")}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">12345678</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default CertDetails;
